import { useEffect, useMemo, useState } from 'react';
import {
  Page, Card, IndexTable, Text, Badge, Banner, Box, BlockStack, InlineStack,
  Pagination, Spinner, Toast, Tooltip, TextField, Icon, Checkbox,
} from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  listTires, getTireStats, createTireJob,
  type InstallSpeed, type TireProduct, type TireVendor, type TireSortBy, type TireListParams,
} from '../api/tires';
import { triggerWheelProsTirePull } from '../api/imports';
import {
  useUrlState, readArray, writeArray, readString, writeString,
  readNumber, writeNumber, readBool, writeBool,
} from '../lib/useUrlState';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { RangeFilter } from '../components/RangeFilter';
import { SortableHeader } from '../components/SortableHeader';
import { StatBadgeRow, type StatBlock } from '../components/StatBadgeRow';
import { FilterChips, type ActiveChip } from '../components/FilterChips';

const PAGE_SIZE = 50;

function formatSizeDisplay(sizeFull: string | null | undefined): string {
  if (!sizeFull) return '';
  const s = String(sizeFull);
  if (/^\d{2}(?:\.\d+)?x\d{1,2}(?:\.\d+)?r\d{2}$/i.test(s)) return s.toUpperCase();
  const m = s.match(/^(p|lt|st)?(\d{3})-(\d{2})-(\d{2})$/i);
  if (m) {
    const prefix = (m[1] ?? '').toUpperCase();
    return `${prefix}${m[2]}/${m[3]}-${m[4]}`;
  }
  return s;
}

const INSTALL_LABEL: Record<InstallSpeed, string> = {
  same_day: 'Same day',
  next_day: '1-3 days out',
  standard: '3-5 days out',
  estimated: 'Estimated',
  out_of_stock: 'Out of stock',
};

const QTY_TOOLTIP =
  'Local / Local Plus / Nationwide — ATD\'s pre-bucketed rollup. ' +
  'Local = stock at our primary DC (Kennesaw GA, locationNumber 914759), confirmed same-day install. ' +
  'Local Plus = regional warehouses ATD considers nearby. ' +
  'Nationwide = combined stock across all other ATD warehouses. ' +
  'ATD does not expose per-warehouse counts via this SOAP method.';

interface TiresFilterState {
  q: string;
  vendor: TireVendor[];
  brand: string[];
  install: InstallSpeed[];
  terrain: string[];
  vehicle_type: string[];
  diameter_min?: number;
  diameter_max?: number;
  needs_review: boolean;
  sort_by: TireSortBy;
  sort_dir: 'asc' | 'desc';
  offset: number;
}

const tiresCodec = {
  read: (p: URLSearchParams): TiresFilterState => ({
    q: readString(p, 'q'),
    vendor: readArray(p, 'vendor') as TireVendor[],
    brand: readArray(p, 'brand'),
    install: readArray(p, 'install') as InstallSpeed[],
    terrain: readArray(p, 'terrain'),
    vehicle_type: readArray(p, 'vehicle'),
    diameter_min: readNumber(p, 'd_min'),
    diameter_max: readNumber(p, 'd_max'),
    needs_review: readBool(p, 'needs_review'),
    sort_by: ((p.get('sort') ?? 'brand') as TireSortBy),
    sort_dir: ((p.get('dir') ?? 'asc') as 'asc' | 'desc'),
    offset: readNumber(p, 'page') !== undefined ? (readNumber(p, 'page')! - 1) * PAGE_SIZE : 0,
  }),
  write: (s: TiresFilterState, p: URLSearchParams) => {
    writeString(p, 'q', s.q);
    writeArray(p, 'vendor', s.vendor);
    writeArray(p, 'brand', s.brand);
    writeArray(p, 'install', s.install);
    writeArray(p, 'terrain', s.terrain);
    writeArray(p, 'vehicle', s.vehicle_type);
    writeNumber(p, 'd_min', s.diameter_min);
    writeNumber(p, 'd_max', s.diameter_max);
    writeBool(p, 'needs_review', s.needs_review);
    if (s.sort_by !== 'brand') p.set('sort', s.sort_by); else p.delete('sort');
    if (s.sort_dir !== 'asc') p.set('dir', s.sort_dir); else p.delete('dir');
    if (s.offset > 0) p.set('page', String(Math.floor(s.offset / PAGE_SIZE) + 1));
    else p.delete('page');
  },
};

const VENDOR_OPTIONS: TireVendor[] = ['ATD', 'WheelPros', 'Manual'];

const INSTALL_OPTIONS: { value: InstallSpeed; label: string }[] = [
  { value: 'same_day',     label: 'Same day' },
  { value: 'next_day',     label: '1-3 days out' },
  { value: 'standard',     label: '3-5 days out' },
  { value: 'estimated',    label: 'Estimated' },
  { value: 'out_of_stock', label: 'Out of stock' },
];

const TERRAIN_OPTIONS: { value: string; label: string }[] = [
  { value: 'ht', label: 'Highway / Touring' },
  { value: 'at', label: 'All-Terrain' },
  { value: 'mt', label: 'Mud-Terrain' },
  { value: 'rt', label: 'Rugged-Terrain' },
];

const VEHICLE_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'car',     label: 'Passenger car' },
  { value: 'truck',   label: 'Truck' },
  { value: 'suv',     label: 'SUV' },
  { value: 'offroad', label: 'Off-road' },
  { value: 'atv',     label: 'ATV' },
  { value: 'utv',     label: 'UTV' },
];

export function TiresList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [toast, setToast] = useState<string | null>(null);

  const [filters, setFilters] = useUrlState<TiresFilterState>(tiresCodec);

  const [searchInput, setSearchInput] = useState(filters.q);
  useEffect(() => { setSearchInput(filters.q); }, [filters.q]);
  const debouncedSearch = useDebouncedValue(searchInput, 250);
  useEffect(() => {
    if (debouncedSearch !== filters.q) {
      setFilters((p) => ({ ...p, q: debouncedSearch, offset: 0 }));
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useQuery({ queryKey: ['tire-stats'], queryFn: getTireStats });

  const params: TireListParams = {
    vendor: filters.vendor.length ? filters.vendor : undefined,
    brand: filters.brand.length ? filters.brand : undefined,
    install_speed: filters.install.length ? filters.install : undefined,
    terrain: filters.terrain.length ? filters.terrain : undefined,
    vehicle_type: filters.vehicle_type.length ? filters.vehicle_type : undefined,
    diameter_min: filters.diameter_min,
    diameter_max: filters.diameter_max,
    needs_review: filters.needs_review || undefined,
    q: filters.q || undefined,
    sort_by: filters.sort_by,
    sort_dir: filters.sort_dir,
    limit: PAGE_SIZE,
    offset: filters.offset,
  };

  const list = useQuery({
    queryKey: ['tires', params],
    queryFn: () => listTires(params),
    placeholderData: keepPreviousData,
  });

  const tires = list.data?.data ?? [];
  const total = list.data?.pagination.total ?? 0;

  const installCounts = stats.data?.by_install_speed;
  const vendorCounts = stats.data?.by_vendor ?? { ATD: 0, WheelPros: 0, Manual: 0 } as Record<string, number>;

  const toggleInstall = (v: InstallSpeed) => {
    setFilters((p) => ({
      ...p,
      install: p.install.includes(v) ? p.install.filter((x) => x !== v) : [v],
      offset: 0,
    }));
  };
  const toggleVendor = (v: TireVendor) => {
    setFilters((p) => ({
      ...p,
      vendor: p.vendor.includes(v) ? p.vendor.filter((x) => x !== v) : [v],
      offset: 0,
    }));
  };
  const toggleNeedsReview = () => {
    setFilters((p) => ({ ...p, needs_review: !p.needs_review, offset: 0 }));
  };

  const installBlocks: StatBlock[] = installCounts ? [
    { id: 'same_day',     label: 'Same day',     count: installCounts.same_day,     tone: 'success',   active: filters.install.includes('same_day'),     onClick: () => toggleInstall('same_day') },
    { id: 'next_day',     label: '1-3 days out', count: installCounts.next_day,     tone: 'info',      active: filters.install.includes('next_day'),     onClick: () => toggleInstall('next_day') },
    { id: 'standard',     label: '3-5 days out', count: installCounts.standard,     tone: 'new',       active: filters.install.includes('standard'),     onClick: () => toggleInstall('standard') },
    { id: 'estimated',    label: 'Estimated',    count: installCounts.estimated,    tone: 'attention', active: filters.install.includes('estimated'),    onClick: () => toggleInstall('estimated') },
    { id: 'out_of_stock', label: 'Out of stock', count: installCounts.out_of_stock, tone: 'critical',  active: filters.install.includes('out_of_stock'), onClick: () => toggleInstall('out_of_stock') },
    { id: 'needs_review', label: 'Needs review', count: stats.data?.needs_review ?? 0, tone: 'warning', active: filters.needs_review,                  onClick: toggleNeedsReview },
  ] : [];

  const vendorBlocks: StatBlock[] = stats.data ? [
    { id: 'ATD',       label: 'ATD',           count: vendorCounts.ATD ?? 0,       tone: 'info',    active: filters.vendor.includes('ATD'),       onClick: () => toggleVendor('ATD') },
    { id: 'WheelPros', label: 'WheelPros',     count: vendorCounts.WheelPros ?? 0, tone: 'info',    active: filters.vendor.includes('WheelPros'), onClick: () => toggleVendor('WheelPros') },
    { id: 'Manual',    label: 'Manual',        count: vendorCounts.Manual ?? 0,    tone: 'info',    active: filters.vendor.includes('Manual'),    onClick: () => toggleVendor('Manual') },
    { id: 'Total',     label: 'Total active',  count: stats.data.total_active ?? 0, tone: 'neutral', active: false,                                onClick: () => setFilters((p) => ({ ...p, vendor: [], offset: 0 })) },
  ] : [];

  // Launch dashboard — Go-Live Ready / Already on Shopify / Pending Push.
  // These tiles are read-only (no toggle filter). Click takes the user
  // back to the unfiltered view so they can drill into the others.
  const launchBlocks: StatBlock[] = stats.data ? [
    { id: 'go_live_ready',  label: 'Go-Live Ready',     count: stats.data.go_live_ready ?? 0,  tone: 'success',   active: false, onClick: () => setFilters((p) => ({ ...p, offset: 0 })) },
    { id: 'pending_push',   label: 'Pending Push',      count: stats.data.pending_push ?? 0,   tone: 'attention', active: false, onClick: () => setFilters((p) => ({ ...p, offset: 0 })) },
    { id: 'already_pushed', label: 'Already on Shopify', count: stats.data.already_pushed ?? 0, tone: 'info',      active: false, onClick: () => setFilters((p) => ({ ...p, offset: 0 })) },
  ] : [];

  const chips: ActiveChip[] = useMemo(() => {
    const out: ActiveChip[] = [];
    for (const v of filters.brand) out.push({
      id: `brand:${v}`, label: `Brand: ${v}`,
      onRemove: () => setFilters((p) => ({ ...p, brand: p.brand.filter((x) => x !== v), offset: 0 })),
    });
    for (const v of filters.vendor) out.push({
      id: `vendor:${v}`, label: `Vendor: ${v}`,
      onRemove: () => setFilters((p) => ({ ...p, vendor: p.vendor.filter((x) => x !== v), offset: 0 })),
    });
    for (const v of filters.install) {
      const lbl = INSTALL_OPTIONS.find((o) => o.value === v)?.label ?? v;
      out.push({
        id: `install:${v}`, label: `Install: ${lbl}`,
        onRemove: () => setFilters((p) => ({ ...p, install: p.install.filter((x) => x !== v), offset: 0 })),
      });
    }
    for (const v of filters.terrain) {
      const lbl = TERRAIN_OPTIONS.find((o) => o.value === v)?.label ?? v;
      out.push({
        id: `terrain:${v}`, label: `Terrain: ${lbl}`,
        onRemove: () => setFilters((p) => ({ ...p, terrain: p.terrain.filter((x) => x !== v), offset: 0 })),
      });
    }
    for (const v of filters.vehicle_type) {
      const lbl = VEHICLE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v;
      out.push({
        id: `vehicle:${v}`, label: `Vehicle: ${lbl}`,
        onRemove: () => setFilters((p) => ({ ...p, vehicle_type: p.vehicle_type.filter((x) => x !== v), offset: 0 })),
      });
    }
    if (filters.diameter_min !== undefined || filters.diameter_max !== undefined) {
      out.push({
        id: 'diameter',
        label: `Diameter: ${filters.diameter_min ?? ''}″–${filters.diameter_max ?? ''}″`,
        onRemove: () => setFilters((p) => ({ ...p, diameter_min: undefined, diameter_max: undefined, offset: 0 })),
      });
    }
    if (filters.needs_review) {
      out.push({
        id: 'needs_review', label: 'Needs review',
        onRemove: () => setFilters((p) => ({ ...p, needs_review: false, offset: 0 })),
      });
    }
    return out;
  }, [filters, setFilters]);

  const clearAll = () => {
    setSearchInput('');
    setFilters({
      q: '', vendor: [], brand: [], install: [], terrain: [], vehicle_type: [],
      diameter_min: undefined, diameter_max: undefined, needs_review: false,
      sort_by: 'brand', sort_dir: 'asc', offset: 0,
    });
  };

  const onSortChange = (next: { key: string; dir: 'asc' | 'desc' }) => {
    setFilters((p) => ({ ...p, sort_by: next.key as TireSortBy, sort_dir: next.dir, offset: 0 }));
  };

  const pullFromAtd = useMutation({
    mutationFn: () => createTireJob({ vendor: 'ATD', source_kind: 'atd_soap', brands: ['*'] }),
    onSuccess: (resp) => {
      setToast(`ATD pull job ${resp.id} started — every brand ATD ships`);
      qc.invalidateQueries({ queryKey: ['tire-jobs'] });
      navigate(`/tires/jobs/${resp.id}`);
    },
    onError: (e: Error) => setToast(`Failed: ${e.message}`),
  });

  // Wheel Pros tire enrichment — runs synchronously against the
  // existing catalog SFTP. Per the operator's policy, this DOES NOT
  // create a second tire catalog: matched WP rows enrich ATD rows in
  // place; unmatched WP rows land as standalone vendor='WheelPros'
  // entries flagged needs_review. Toyo is hard-blocked.
  const pullWpTires = useMutation({
    mutationFn: () => triggerWheelProsTirePull(),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['tires'] });
      qc.invalidateQueries({ queryKey: ['tire-stats'] });
      setToast(
        `WP enrichment done — ${r.matched.total.toLocaleString()} ATD rows enriched ` +
        `(UPC ${r.matched.upc} / spec ${r.matched.brand_size_load_speed} / model ${r.matched.brand_size_model}), ` +
        `${r.unmatched_inserted.toLocaleString()} new WP-only rows, ${r.toyo_blocked} Toyo blocked`
      );
    },
    onError: (e: Error) => setToast(`WP tire pull failed: ${e.message}`),
  });

  const brandOptions = (stats.data?.brands ?? [])
    .slice(0, 200)
    .map((b: { brand: string; count: number }) => ({ value: b.brand, label: b.brand, count: b.count }));

  return (
    <>
      <Page
        title="Tires"
        subtitle={`${total.toLocaleString()} record${total === 1 ? '' : 's'}`}
        primaryAction={{
          content: pullFromAtd.isPending ? 'Pulling everything ATD ships…' : 'Pull every ATD tire',
          onAction: () => pullFromAtd.mutate(),
          loading: pullFromAtd.isPending,
          disabled: pullFromAtd.isPending,
          helpText: 'Sends a brand-less query for each tire product group — every brand ATD has, not just the curated list. Toyo excluded. Auto-applies to tire_products.',
        }}
        secondaryActions={[
          {
            content: pullWpTires.isPending ? 'Enriching from Wheel Pros…' : 'Enrich tires from Wheel Pros',
            onAction: () => {
              if (!confirm('Pull Wheel Pros tire feed and enrich matching ATD rows? Unmatched tires land as standalone WP rows (out_of_stock). Toyo is hard-blocked. Runs synchronously (~30 sec).')) return;
              pullWpTires.mutate();
            },
            loading: pullWpTires.isPending,
            disabled: pullWpTires.isPending,
          },
          { content: 'Tire imports', onAction: () => navigate('/tires/jobs') },
        ]}
      >
        {list.error && (
          <Box paddingBlockEnd="400">
            <Banner tone="critical">Failed to load tires: {(list.error as Error).message}</Banner>
          </Box>
        )}

        <BlockStack gap="400">
          {/* Launch dashboard — Go-Live Ready / Pending Push / Already on Shopify */}
          {launchBlocks.length > 0 && <StatBadgeRow blocks={launchBlocks} />}

          {/* Vendor breakdown — clickable */}
          {vendorBlocks.length > 0 && <StatBadgeRow blocks={vendorBlocks} />}

          {/* Install-speed strip — clickable */}
          {installBlocks.length > 0 && <StatBadgeRow blocks={installBlocks} />}

          <Card padding="0">
            <Box padding="400">
              <BlockStack gap="300">
                <InlineStack gap="200" wrap blockAlign="center">
                  <Box minWidth="320px">
                    <TextField
                      label=""
                      labelHidden
                      value={searchInput}
                      onChange={setSearchInput}
                      placeholder="Search SKU, brand, model, size (e.g. 195/65-15)…"
                      prefix={<Icon source={SearchIcon} />}
                      autoComplete="off"
                      clearButton
                      onClearButtonClick={() => setSearchInput('')}
                    />
                  </Box>
                  <MultiSelectFilter
                    label="Brand"
                    searchable
                    searchPlaceholder="Search brands…"
                    selected={filters.brand}
                    options={brandOptions}
                    onChange={(next) => setFilters((p) => ({ ...p, brand: next, offset: 0 }))}
                  />
                  <MultiSelectFilter
                    label="Vendor"
                    selected={filters.vendor}
                    options={VENDOR_OPTIONS.map((v) => ({
                      value: v, label: v, count: vendorCounts[v] ?? 0,
                    }))}
                    onChange={(next) => setFilters((p) => ({ ...p, vendor: next as TireVendor[], offset: 0 }))}
                  />
                  <MultiSelectFilter
                    label="Install"
                    selected={filters.install}
                    options={INSTALL_OPTIONS.map((o) => ({
                      value: o.value,
                      label: o.label,
                      count: installCounts ? (installCounts as any)[o.value] : undefined,
                    }))}
                    onChange={(next) => setFilters((p) => ({ ...p, install: next as InstallSpeed[], offset: 0 }))}
                  />
                  <MultiSelectFilter
                    label="Terrain"
                    selected={filters.terrain}
                    options={TERRAIN_OPTIONS}
                    onChange={(next) => setFilters((p) => ({ ...p, terrain: next, offset: 0 }))}
                  />
                  <MultiSelectFilter
                    label="Vehicle"
                    selected={filters.vehicle_type}
                    options={VEHICLE_TYPE_OPTIONS}
                    onChange={(next) => setFilters((p) => ({ ...p, vehicle_type: next, offset: 0 }))}
                  />
                  <RangeFilter
                    label="Rim diameter"
                    unit={'"'}
                    min={filters.diameter_min}
                    max={filters.diameter_max}
                    onChange={({ min, max }) => setFilters((p) => ({ ...p, diameter_min: min, diameter_max: max, offset: 0 }))}
                  />
                  <Checkbox
                    label="Needs review"
                    checked={filters.needs_review}
                    onChange={toggleNeedsReview}
                  />
                </InlineStack>
                <FilterChips chips={chips} onClearAll={clearAll} />
              </BlockStack>
            </Box>

            {(list.isLoading && !list.data) ? (
              <Box padding="600"><InlineStack align="center"><Spinner /></InlineStack></Box>
            ) : (
              <IndexTable
                resourceName={{ singular: 'tire', plural: 'tires' }}
                itemCount={tires.length}
                selectable={false}
                loading={list.isFetching && !!list.data}
                headings={[
                  { title: <SortableHeader label="SKU"     sortKey="sku"     activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
                  { title: <SortableHeader label="Brand"   sortKey="brand"   activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
                  { title: <SortableHeader label="Model"   sortKey="model"   activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
                  { title: 'Source' },
                  { title: <SortableHeader label="Size"    sortKey="size"    activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
                  { title: <SortableHeader label="Terrain" sortKey="terrain" activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
                  { title: <SortableHeader label="Vehicle" sortKey="vehicle" activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
                  { title: <SortableHeader label="Install" sortKey="install" activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
                  { title: <SortableHeader label="Qty"     sortKey="qty"     activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
                  { title: <SortableHeader label="Price"   sortKey="price"   activeKey={filters.sort_by} activeDir={filters.sort_dir} onChange={onSortChange} /> as any },
                  { title: 'Review' },
                ]}
              >
                {tires.map((t, i) => (
                  <IndexTable.Row id={String(t.id)} key={t.id} position={i} onClick={() => navigate(`/tires/${t.id}`)}>
                    <IndexTable.Cell>{t.vendor_sku}</IndexTable.Cell>
                    <IndexTable.Cell>{t.brand}</IndexTable.Cell>
                    <IndexTable.Cell>{t.model}</IndexTable.Cell>
                    <IndexTable.Cell><Badge>{t.vendor}</Badge></IndexTable.Cell>
                    <IndexTable.Cell>{t.size_display ?? formatSizeDisplay(t.tire_size_full)}</IndexTable.Cell>
                    <IndexTable.Cell>{t.terrain ?? <Text as="span" tone="subdued">—</Text>}</IndexTable.Cell>
                    <IndexTable.Cell>{t.vehicle_type ?? <Text as="span" tone="subdued">—</Text>}</IndexTable.Cell>
                    <IndexTable.Cell><InstallBadge tier={t.install_speed} /></IndexTable.Cell>
                    <IndexTable.Cell>
                      <Tooltip content={QTY_TOOLTIP}>
                        <Text as="span" variant="bodySm" tone="subdued">
                          {fmtQty(t.qty_local)} / {fmtQty(t.qty_local_plus)} / {fmtQty(t.qty_nationwide)}
                        </Text>
                      </Tooltip>
                    </IndexTable.Cell>
                    <IndexTable.Cell>${(t.price_cents / 100).toFixed(2)}</IndexTable.Cell>
                    <IndexTable.Cell>
                      {t.needs_review ? (
                        <Badge tone="warning">{String((t.needs_review_reasons ?? []).length)}</Badge>
                      ) : (
                        <Text as="span" tone="subdued">—</Text>
                      )}
                    </IndexTable.Cell>
                  </IndexTable.Row>
                ))}
              </IndexTable>
            )}

            <Box padding="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="span" variant="bodySm" tone="subdued">
                  {total === 0 ? '0 of 0' : `${filters.offset + 1}–${Math.min(filters.offset + tires.length, total)} of ${total.toLocaleString()}`}
                </Text>
                <Pagination
                  hasPrevious={filters.offset > 0}
                  onPrevious={() => setFilters((p) => ({ ...p, offset: Math.max(0, p.offset - PAGE_SIZE) }))}
                  hasNext={filters.offset + PAGE_SIZE < total}
                  onNext={() => setFilters((p) => ({ ...p, offset: p.offset + PAGE_SIZE }))}
                />
              </InlineStack>
            </Box>
          </Card>
        </BlockStack>
      </Page>
      {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
    </>
  );
}

function InstallBadge({ tier }: { tier: TireProduct['install_speed'] }) {
  const tone =
    tier === 'same_day' ? 'success' :
    tier === 'next_day' ? 'info' :
    tier === 'out_of_stock' ? 'critical' :
    tier === 'estimated' ? 'attention' :
    'new';
  return <Badge tone={tone}>{INSTALL_LABEL[tier] ?? tier}</Badge>;
}

function fmtQty(n: number | null) {
  if (n === null || n === undefined) return '—';
  return String(n);
}
